import { Dir, dx, dy, opposite, type Direction } from '../../sim/direction';
import { needsMowing } from '../../sim/grid';
import type { SimLevel } from '../../sim/level';
import type { MowerSpec } from '../../sim/mower';
import { Simulation } from '../../sim/simulation';

export interface StormResult {
  won: boolean;
  caught: boolean;
  /** Share of the lawn's grass mowed (0..1). */
  mowedShare: number;
  ticks: number;
  crashes: number;
  /** Tile centers reached, with the fuel each step burned (for placing gas cans). */
  route: { x: number; y: number; fuel: number }[];
}

/** Rows of rain-free lawn the bot keeps between itself and the storm before it stops sweeping. */
const SAFE_ROWS = 3;

/**
 * Steers a mower through a Beat the Storm lawn the way a sensible player would: sweep each row side
 * to side, one row up at a time (a lawn-mowing zig-zag), and head straight for the top when the
 * rain gets close. Drives the storm bot and the in-game autopilot on storm lawns.
 */
export class StormPilot {
  private sweep: Direction;
  private climbing = false;

  constructor(private readonly sim: Simulation) {
    this.sweep = sim.mower.x < sim.grid.width / 2 ? Dir.E : Dir.W;
  }

  /** At a tile center: the heading to ask for (or null to keep going). */
  decide(): Direction | null {
    const { sim } = this;
    const m = sim.mower;
    if (m.progress !== 0) return null;
    const grid = sim.grid;
    const cutPower = sim.mowerSpec.cutPower;
    const open = (x: number, y: number) =>
      grid.inBounds(x, y) && !grid.isBlockedFor(x, y, cutPower);
    /** Grass still to mow along row `y` from `x` heading `d`, before something blocks the way. */
    const grassAhead = (x: number, y: number, d: Direction) => {
      for (let cx = x + dx(d); open(cx, y); cx += dx(d))
        if (needsMowing(grid.tileAt(cx, y))) return true;
      return false;
    };
    const storm = sim.stormRow(sim.tick + sim.mowerSpec.ticksPerTile * 2) ?? grid.height;
    const close = storm - m.y <= SAFE_ROWS;
    if (this.climbing) {
      // Just came up a row: sweep back the other way.
      this.climbing = false;
      this.sweep = opposite(this.sweep);
    }
    // Preferences, best first; never into something, never a reverse (most mowers can't).
    const sweep = this.sweep;
    const sweeping = !close && grassAhead(m.x, m.y, sweep);
    const prefs: Direction[] = sweeping
      ? [sweep, Dir.N, Dir.S]
      : [Dir.N, sweep, opposite(sweep), Dir.S];
    const options = prefs.filter((d) => d !== opposite(m.dir) && open(m.x + dx(d), m.y + dy(d)));
    const want: Direction = options[0] ?? m.dir;
    if (want === Dir.N && !sweeping && !close) this.climbing = true;
    if (want !== Dir.N && want !== sweep && want !== Dir.S) this.sweep = want;
    return want === m.dir ? null : want;
  }
}

/** Plays a storm lawn with the {@link StormPilot}: is it winnable, and how much gets mowed? */
export function solveStorm(
  level: SimLevel,
  mower: MowerSpec,
  { maxTicks = 100_000, ignoreFuel = false }: { maxTicks?: number; ignoreFuel?: boolean } = {},
): StormResult {
  // An "endless" tank is a huge one, so the fuel each step burns can still be measured.
  const spec: MowerSpec =
    ignoreFuel && mower.fuelTiles !== undefined ? { ...mower, fuelTiles: 1_000_000_000 } : mower;
  const sim = new Simulation(level, spec);
  sim.drainEvents();
  const grid = sim.grid;
  const pilot = new StormPilot(sim);
  let crashes = 0;
  const route = [{ x: sim.mower.x, y: sim.mower.y, fuel: 0 }];
  let fuel = sim.mower.fuel;

  while (sim.status === 'running' && sim.tick < maxTicks) {
    const want = pilot.decide();
    if (want !== null) sim.enqueue({ type: 'face', dir: want });
    for (const e of sim.step()) if (e.type === 'crashed') crashes++;
    const now = sim.mower;
    const last = route[route.length - 1];
    if (now.progress === 0 && (last?.x !== now.x || last.y !== now.y)) {
      route.push({
        x: now.x,
        y: now.y,
        fuel: Number.isFinite(now.fuel) && now.fuel < fuel ? fuel - now.fuel : 1,
      });
    }
    fuel = now.fuel;
  }
  const total = grid.totalGrass;
  return {
    won: sim.status === 'won',
    caught: sim.status === 'caught',
    mowedShare: total === 0 ? 1 : (total - grid.grassRemaining) / total,
    ticks: sim.tick,
    crashes,
    route,
  };
}
